import React from 'react';
import { Box, Container, Typography, Grid, Paper, Button, Stack } from '@mui/material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import HeroSlider from '../components/HeroSlider';
import SectionHeading from '../components/SectionHeading';
import { gradients } from '../theme/theme';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import HubIcon from '@mui/icons-material/Hub';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const HomePage = () => {
  const { t } = useTranslation();

  const highlights = [
    { icon: PhoneInTalkIcon, label: t('services.items.telecom.title'), to: '/hizmetler', color: '#1565C0' },
    { icon: LocalShippingIcon, label: t('services.items.logistics.title'), to: '/hizmetler', color: '#00897B' },
    { icon: BusinessCenterIcon, label: t('services.items.consulting.title'), to: '/hizmetler', color: '#5C6BC0' },
    { icon: FlightTakeoffIcon, label: t('services.items.tourism.title'), to: '/hizmetler', color: '#FF7043' },
  ];

  const stats = [
    { ...t('home.stats.experience', { returnObjects: true }) },
    { ...t('home.stats.sectors', { returnObjects: true }) },
    { ...t('home.stats.support', { returnObjects: true }) },
    { ...t('home.stats.focus', { returnObjects: true }) },
  ];

  const whyItems = [
    { icon: VerifiedUserIcon, key: 'trusted', color: '#1565C0' },
    { icon: HubIcon, key: 'integrated', color: '#00897B' },
    { icon: WorkspacePremiumIcon, key: 'quality', color: '#5C6BC0' },
    { icon: SupportAgentIcon, key: 'support', color: '#FF7043' },
  ];

  return (
    <Box>
      <HeroSlider />

      {/* About teaser — who we are */}
      <Box sx={{ py: { xs: 7, md: 11 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 5, md: 7 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Box sx={{ position: 'relative', pr: { md: 3 }, pb: { xs: 4, md: 3 } }}>
                  <Box
                    component="img"
                    src="/about/team-4344114.jpg"
                    alt={t('home.about.title')}
                    loading="lazy"
                    sx={{
                      width: '100%',
                      borderRadius: 5,
                      display: 'block',
                      boxShadow: '0 40px 80px -40px rgba(13,71,161,0.55)',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: { xs: 0, md: 12 },
                      right: { xs: 8, md: -8 },
                      bgcolor: 'background.paper',
                      borderRadius: 4,
                      px: 3,
                      py: 1.75,
                      textAlign: 'center',
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: '0 24px 50px -24px rgba(16,32,50,0.45)',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: '"Plus Jakarta Sans", sans-serif',
                        fontWeight: 800,
                        fontSize: '2rem',
                        lineHeight: 1,
                        color: 'primary.main',
                      }}
                    >
                      {t('home.about.badgeValue')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                      {t('home.about.badgeLabel')}
                    </Typography>
                  </Box>
                </Box>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Typography variant="overline" sx={{ color: 'primary.main', mb: 1.5, display: 'block' }}>
                  {t('home.about.eyebrow')}
                </Typography>
                <Typography variant="h3" sx={{ mb: 2.5, color: 'text.primary' }}>
                  {t('home.about.title')}
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3.5, fontSize: '1.08rem', lineHeight: 1.85 }}>
                  {t('home.intro')}
                </Typography>
                <Stack spacing={1.5} sx={{ mb: 4 }}>
                  {['f1', 'f2', 'f3', 'f4'].map((f) => (
                    <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <CheckCircleIcon sx={{ color: 'primary.main', fontSize: 22, flexShrink: 0 }} />
                      <Typography sx={{ color: 'text.primary', fontWeight: 500 }}>
                        {t(`home.about.features.${f}`)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
                <Button component={Link} to="/hakkimizda" variant="contained" size="large" endIcon={<ArrowForwardIcon />}>
                  {t('home.about.button')}
                </Button>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Services */}
      <Box sx={{ py: { xs: 7, md: 11 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow={t('home.servicesEyebrow')}
            title={t('home.servicesTitle')}
            subtitle={t('home.servicesDesc')}
            sx={{ mb: 6 }}
          />

          <Grid container spacing={3} sx={{ mb: 6 }}>
            {highlights.map((item, index) => (
              <Grid item xs={6} md={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  style={{ height: '100%' }}
                >
                  <Paper
                    component={Link}
                    to={item.to}
                    elevation={0}
                    sx={{
                      display: 'block',
                      height: '100%',
                      p: { xs: 2.5, md: 3.5 },
                      textAlign: 'center',
                      borderRadius: 4,
                      textDecoration: 'none',
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                      transition: 'all 0.35s cubic-bezier(0.22,1,0.36,1)',
                      '&:hover': {
                        borderColor: item.color,
                        transform: 'translateY(-6px)',
                        boxShadow: `0 22px 45px -22px ${item.color}99`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${item.color}1A, ${item.color}33)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                      }}
                    >
                      <item.icon sx={{ fontSize: 32, color: item.color }} />
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {item.label}
                    </Typography>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button component={Link} to="/hizmetler" variant="contained" size="large" endIcon={<ArrowForwardIcon />} sx={{ px: 4, py: 1.5 }}>
              {t('nav.services')}
            </Button>
            <Button component={Link} to="/iletisim" variant="outlined" size="large" sx={{ px: 4, py: 1.5 }}>
              {t('nav.contact')}
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Stats Band */}
      <Box sx={{ position: 'relative', py: { xs: 6, md: 8 }, background: gradients.hero, overflow: 'hidden' }}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.5,
            background:
              'radial-gradient(circle at 15% 20%, rgba(255,255,255,0.12), transparent 40%), radial-gradient(circle at 85% 80%, rgba(255,122,69,0.18), transparent 45%)',
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative' }}>
          <Grid container spacing={{ xs: 3, md: 2 }}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Box sx={{ textAlign: 'center', color: 'white' }}>
                    <Typography
                      sx={{
                        fontFamily: '"Plus Jakarta Sans", sans-serif',
                        fontWeight: 800,
                        fontSize: { xs: '2.2rem', md: '3rem' },
                        lineHeight: 1,
                        mb: 1,
                        background: 'linear-gradient(135deg, #ffffff, #cfe3ff)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.82)', fontWeight: 500, fontSize: { xs: '0.85rem', md: '0.95rem' } }}>
                      {stat.label}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Why Choose Us — compact diagram */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow={t('home.why.eyebrow')}
            title={t('home.why.title')}
            subtitle={t('home.why.subtitle')}
            sx={{ mb: { xs: 5, md: 6 } }}
          />

          <Box sx={{ position: 'relative', display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
            {/* connector line (desktop) */}
            <Box
              sx={{
                display: { xs: 'none', md: 'block' },
                position: 'absolute',
                top: 34,
                left: '12.5%',
                right: '12.5%',
                height: 2,
                background:
                  'linear-gradient(90deg, rgba(21,101,192,0.25), rgba(0,137,123,0.25), rgba(92,107,192,0.25), rgba(255,112,67,0.25))',
                zIndex: 0,
              }}
            />

            {whyItems.map((item, index) => (
              <Box
                key={item.key}
                component={motion.div}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: index * 0.12 }}
                viewport={{ once: true }}
                sx={{
                  flexBasis: { xs: '50%', md: '25%' },
                  maxWidth: { xs: '50%', md: '25%' },
                  px: { xs: 1.5, md: 2 },
                  pb: 3,
                  textAlign: 'center',
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                <Box
                  sx={{
                    width: 68,
                    height: 68,
                    mx: 'auto',
                    mb: 1.75,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${item.color}, ${item.color}cc)`,
                    border: '5px solid #fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 14px 28px -12px ${item.color}aa`,
                    transition: 'transform 0.35s ease',
                    '&:hover': { transform: 'translateY(-4px) scale(1.05)' },
                  }}
                >
                  <item.icon sx={{ fontSize: 30, color: 'white' }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary' }}>
                  {t(`home.why.items.${item.key}.title`)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', lineHeight: 1.6, maxWidth: 240, mx: 'auto' }}
                >
                  {t(`home.why.items.${item.key}.description`)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Band */}
      <Box sx={{ py: { xs: 7, md: 9 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Box
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 6,
                px: { xs: 3, md: 8 },
                py: { xs: 5, md: 7 },
                background: gradients.primary,
                boxShadow: '0 30px 60px -30px rgba(13,71,161,0.6)',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  opacity: 0.4,
                  background:
                    'radial-gradient(circle at 85% 15%, rgba(255,122,69,0.35), transparent 40%)',
                }}
              />
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={4}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
                sx={{ position: 'relative' }}
              >
                <Box sx={{ maxWidth: 620 }}>
                  <Typography variant="h3" sx={{ color: 'white', mb: 1.5 }}>
                    {t('home.cta.title')}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '1.05rem', lineHeight: 1.7 }}>
                    {t('home.cta.description')}
                  </Typography>
                </Box>
                <Button
                  component={Link}
                  to="/iletisim"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    flexShrink: 0,
                    bgcolor: 'white',
                    color: 'primary.dark',
                    px: 4,
                    py: 1.6,
                    fontSize: '1rem',
                    fontWeight: 700,
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)', transform: 'translateY(-2px)' },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {t('home.cta.button')}
                </Button>
              </Stack>
            </Box>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;
